import { DialogRef } from '@angular/cdk/dialog';
import { Component, computed, Injector, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { filter, take, takeWhile } from 'rxjs';
import { AppInfo } from '../../../../../global/environment';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { DialogService } from '../../../../../shared/components/dialog/services/dialog.service';
import { FormDirective } from '../../../../../shared/directives/form.directive';
import { GAService } from '../../services/ga-tags.service';
import { OrbitFormClaimService } from '../../services/orbit-form-claim.service';
import { ApplianceDetailsComponent } from './components/appliance-details/appliance-details.component';
import { BookCollectionComponent } from './components/book-collection/book-collection.component';
import { BookEngineerComponent } from './components/book-engineer/book-engineer.component';
import { ConfirmYourDetailsComponent } from './components/confirm-your-details/confirm-your-details.component';
import { FaultDetailsComponent } from './components/fault-details/fault-details.component';
import { OrbitQaFlowComponent } from './components/orbit-qa-flow/orbit-qa-flow.component';
import { PaymentFormComponent } from './components/payment-form/payment-form.component';
import { ProductNumberCodeComponent } from './components/product-number-code/product-number-code.component';
import { ServiceOptionsComponent } from './components/service-options/service-options.component';
import { getErrorsFromSubmission } from './pipes/error-subscription';

@Component({
  selector: 'app-orbit-claims-form',
  standalone: true,
  imports: [
    ApplianceDetailsComponent,
    ProductNumberCodeComponent,
    FormDirective,
    FormsModule,
    ButtonComponent,
    FaultDetailsComponent,
    OrbitQaFlowComponent,
    ConfirmYourDetailsComponent,
    ServiceOptionsComponent,
    BookEngineerComponent,
    BookCollectionComponent,
    PaymentFormComponent,
  ],
  templateUrl: './orbit-claims-form.component.html',
  styleUrl: './orbit-claims-form.component.scss',
})
export class OrbitClaimsFormComponent implements OnInit, OnDestroy {
  currentStep = signal<string>('');
  formValue = signal<Partial<Record<string, any>>>({});
  bookEngineerComponent = viewChild<BookEngineerComponent | undefined>('bookEngineerComponent');
  faultDetailsComponent = viewChild<FaultDetailsComponent>('faultDetailsComponent');
  applianceDetailComponent = viewChild<ApplianceDetailsComponent>('applianceDetailsComponent');
  orbitQaFlowComponent = viewChild<OrbitQaFlowComponent>('orbitQaFlowComponent');
  private _destroyed = false;

  stageSubmitted = signal<boolean>(false);
  formValid = signal(true);
  appointmentData = signal<any>({});
  appointmentDay = signal<any>({});

  apiAttemptCount = signal(0);
  showTimeoutMessage = signal<boolean>(false);
  private requestTimer: any;
  private loadingDialogRef: DialogRef<void> | undefined;
  confirmBlankModelNumber = false;

  private viewModel = computed(() => {
    const goodsColour = this.orbitFormClaimService.state?.claimData?.bundle?.goodsColour;
    return {
      currentStep: this.currentStep(),
      formValid: this.formValid(),
      stageSubmitted: this.stageSubmitted(),
      formValue: this.formValue(),
      submittingValue: this.orbitFormClaimService.formSubmission.isLoading(),
      loadingData: this.orbitFormClaimService.repairDataLoading(),
      showActionSection: this.currentStep() !== 'payment',
      showBackButton:
        !this.orbitFormClaimService.state.claimData.claimCreated &&
        this.orbitFormClaimService.state.claimData.stepsToGoThrough.length &&
        this.orbitFormClaimService.state.claimData.stepsToGoThrough.indexOf(this.currentStep()) > 0,
      bookEngineerComponent: this.bookEngineerComponent(),
      smartFixEmbeddedFixComponent: this.faultDetailsComponent()?.smartFixEmbeddedFix(),
      applianceDetailComponent: this.applianceDetailComponent(),
      submittingResource: this.orbitFormClaimService.formSubmission,
      showSlowLoadingMessage:
        this.currentStep() === 'confirm-your-details' && (this.apiAttemptCount() > 0 || this.showTimeoutMessage()),
      appointmentData: this.appointmentData(),
      appointmentDay: this.appointmentDay(),
      mmrLink: `${AppInfo.Environment.claimsURL}/my-plans/${this.route.snapshot.params['planNumber']}/claims`,
      isHeatingPlan: goodsColour === 'H',
      showIUnderstand: !!this.orbitQaFlowComponent()?.blockingAnswerMessage,
    };
  });

  get vm() {
    return this.viewModel();
  }

  constructor(
    private readonly injector: Injector,
    private readonly route: ActivatedRoute,
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly gaService: GAService,
    private readonly dialogService: DialogService
  ) {}

  ngOnDestroy(): void {
    this._destroyed = true;
    this.closeLoadingDialog();
    this.clearTimeoutMessage();
  }

  ngOnInit(): void {
    // LiveChat.showLiveChatLauncher();
    this.currentStep.set(this.route.snapshot.params['step']);

    this.orbitFormClaimService.currentStep.set(this.route.snapshot.params['step']);
    this.handleAPIErrors();
    this.handleLoadingStateChanges();
    this.listenToStepChange();
    this.getStoredFormValue();
  }

  getStoredFormValue(): void {
    this.orbitFormClaimService.initialFormValueLoaded
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe({
        next: (val) => {
          this.formValue.set(val);
          this.stageSubmitted.set(false);
          this.clearTimeoutMessage();
        },
      });
  }

  listenToStepChange(): void {
    this.route.params.pipe(takeWhile(() => !this._destroyed)).subscribe({
      next: ({ step }) => {
        this.orbitFormClaimService.currentStep.set(step);
        this.currentStep.set(step);

        const convertedText = this.currentStep().replace(/-/g, ' ');
        const formattedStep = convertedText.charAt(0).toUpperCase() + convertedText.substring(1, convertedText.length);
        setTimeout(() => {
          document.title = `${formattedStep} | Domestic & General`;
        });

        this.getStoredFormValue();
      },
    });
  }

  goBack(): void {
    this.orbitFormClaimService.moveBackStep(this.vm.currentStep!);
    this.getStoredFormValue();
  }

  handleIUnderstand(): void {
    this.formValid.set(true);
    this.submitFormValue();
  }

  handleContinueClicked(): void {
    if (this.vm.showIUnderstand) {
      this.handleIUnderstand();
    } else {
      this.trackContinueClicked(this.vm.currentStep);
    }
  }

  handleFormSubmit(): void {
    if (!this.vm.showIUnderstand) {
      this.submitFormValue();
    }
  }

  get continueLabelText(): string {
    if (this.vm.showIUnderstand) return 'I understand';

    switch (this.vm.currentStep) {
      case 'book-engineer': {
        const bec = this.bookEngineerComponent()?.vm;
        if (bec?.retryError || (bec?.isRetryLoop && !bec?.isAtMaxRetrys)) return 'Retry this booking';
        if (bec?.isAtMaxRetrys) return 'Back to repairs page';
        if (bec?.isAllDaySlots) return 'Confirm booking';
        return 'Book this time';
      }
      case 'book-collection':
        return 'Book this time';
      case 'confirm-your-details':
        return 'Confirm';
      case 'service-options':
        return 'Confirm';
      case 'model-number':
        return this.continueTitle;
      case 'fault-details': {
        const smartFixEmbeddedContentPanel = this.faultDetailsComponent()?.smartFixEmbeddedFix();
        if (smartFixEmbeddedContentPanel?.cancelRepair()) return 'Cancel booking';
        return 'Continue';
      }
      default:
        return 'Continue';
    }
  }

  get showContinueArrow(): boolean {
    return (
      this.vm.showIUnderstand ||
      this.vm.currentStep !== 'fault-details' ||
      !this.faultDetailsComponent()?.smartFixEmbeddedFix()?.cancelRepair()
    );
  }

  get continueTitle(): string {
    if (
      this.vm.applianceDetailComponent?.confirmBlankModelNumber() &&
      !this.orbitFormClaimService.state.modelNumberMandatory
    )
      return 'Continue anyway';
    return this.orbitFormClaimService.state.stillCantFindNumber ? 'Continue anyway' : 'Continue';
  }

  handleAPIErrors(): void {
    getErrorsFromSubmission(
      this.vm.submittingResource,
      this.injector,
      (attempts) => {
        this.apiAttemptCount.set(attempts);
        this.clearTimeoutMessage();
      },
      {
        autoRetry: false,
      }
    );
  }

  submitFormValue(): void {
    if (this.vm.bookEngineerComponent?.vm?.isAtMaxRetrys) {
      window.location.href = this.vm.mmrLink;
      return;
    }

    const isBVModelNumberLookupandError =
      this.orbitFormClaimService.state.bvModelNumberLookup && this.applianceDetailComponent()?.apiLookupError();
    const optionalModelNumber = !this.orbitFormClaimService.state.modelNumberMandatory;
    if (
      this.vm.currentStep === 'model-number' &&
      !this.confirmBlankModelNumber &&
      (isBVModelNumberLookupandError || optionalModelNumber) &&
      !this.vm.formValue?.['model-number']?.['modelNumber']
    ) {
      this.confirmBlankModelNumber = true;
      return;
    }

    if (this.vm.currentStep === 'fault-details') {
      const smartFixEmbeddedContentPanel = this.faultDetailsComponent()?.smartFixEmbeddedFix();
      if (smartFixEmbeddedContentPanel && !smartFixEmbeddedContentPanel.handleValidationAndContinue()) {
        return;
      }
    }
    this.stageSubmitted.set(true);

    if (!this.vm.formValid) {
      return;
    }
    this.showTimeoutMessage.set(false);

    this.orbitFormClaimService.storeValue(this.vm.currentStep!, this.vm.formValue[this.vm.currentStep!]);

    // This will work out if there is another step in the form, otherwise it will submit step
    this.orbitFormClaimService.moveStepForward(this.vm.currentStep!);
    this.getStoredFormValue();
  }

  trackContinueClicked(step: string) {
    if (!this.vm.formValid) {
      return;
    }
    switch (step) {
      case 'model-number':
        this.gaService.sendGATag({
          ...this.gaService.getContinueClickedData(step),
          productModel: this.vm.formValue?.['model-number']?.['modelNumber'],
        });
        break;
      case 'product-number':
        this.gaService.sendGATag({
          ...this.gaService.getContinueClickedData(step),
          productModel: this.vm.formValue?.['product-number']?.['PNCNumber']?.dataLabel,
        });
        break;
      case 'fault-details': {
        const smartFixEmbeddedPanel = this.faultDetailsComponent()?.smartFixEmbeddedFix();
        if (smartFixEmbeddedPanel) {
          const eventLabel = smartFixEmbeddedPanel.cancelRepair()
            ? 'Cancel booking'
            : smartFixEmbeddedPanel.videoOpen
              ? 'Continue booking'
              : 'No, continue booking';
          this.gaService.sendGATag({
            ...this.gaService.getContinueClickedData('fault-details-smart-fix-embedded-panel', eventLabel),
          });
          break;
        }
        if (this.vm.formValue['fault-details']?.freeTextSearch?.dataLabel) {
          const applianceUsable = this.vm.formValue['fault-details']?.applianceUsable;
          this.sendFaultGATag(step, applianceUsable, true);
        } else if (this.vm.formValue['fault-details']?.faultDetails?.faultLabel) {
          const applianceUsable = this.vm.formValue['fault-details']?.applianceUsable;
          this.sendFaultGATag(step, applianceUsable, false);
        } else {
          this.gaService.sendGATag({
            ...this.gaService.getContinueClickedData(step),
            eventAction: 'Continue',
            eventLabel: '',
          });
        }
        break;
      }
      case 'repair-details':
        const date = Object.values(this.vm.formValue['repair-details'])[0];
        // Send GA tag for fault start date only
        if (this.checkDateFormat(date)) {
          this.gaService.sendGATag({
            ...this.gaService.getContinueClickedData(step),
            eventLabel: date,
          });
        }
        break;
      case 'confirm-your-details':
        this.gaService.sendGATag({
          ...this.gaService.getContinueClickedData(step),
        });
        break;
      case 'service-options':
        break;
      case 'book-engineer':
        const selectedSlot = this.vm.formValue['book-engineer'].selectedSlot;
        this.gaService.sendGATag({
          ...this.gaService.getContinueClickedData(step),
          eventLabel: `Selected:${this.vm.appointmentDay?.selectedDay || ''}| ClaimsFirstAvailableDate:${this.vm.appointmentData?.minDate}| Timeslot:${selectedSlot?.startTime && selectedSlot?.endTime ? selectedSlot?.startTime + '-' + selectedSlot?.endTime : ''}| TotalAvailable:${this.vm.appointmentData?.totalActiveDates}| Block:${this.vm.appointmentData?.block}| Length:${this.vm.appointmentData?.numberOfActiveDatesThisMonth}| Available:${this.vm.appointmentData?.numberOfActiveDatesThisMonth || ''}`,
        });
        break;
      default:
        this.gaService.sendGATag(this.gaService.getContinueClickedData(step));
    }
  }

  sendFaultGATag(step: string, applianceUsable: boolean, freeTextSearch: boolean) {
    const baseData = this.gaService.getContinueClickedData(step);
    const faultData = this.vm.formValue['fault-details'] ?? {};
    const freeTextLabel = faultData?.freeTextSearch?.dataLabel ?? '';
    const faultLabel = faultData?.faultDetails?.faultLabel ?? '';

    if (applianceUsable === undefined) {
      this.gaService.sendGATag({
        ...baseData,
        eventAction: 'Continue',
        eventLabel: freeTextLabel,
      });
      return;
    }

    const eventAction = freeTextSearch ? freeTextLabel : faultLabel;

    const eventLabel = `${applianceUsable ? 'Yes, I can still use it' : "No, I can't use it"} - Continue`;

    this.gaService.sendGATag({
      ...baseData,
      eventAction,
      eventLabel,
    });
  }

  setAppointmentData(data: any) {
    this.appointmentData.set(data);
  }

  setDate(data: any) {
    this.appointmentDay.set(data);
  }

  private handleLoadingStateChanges(): void {
    toObservable(this.orbitFormClaimService.formSubmission.isLoading, { injector: this.injector })
      .pipe(takeWhile(() => !this._destroyed))
      .subscribe({
        next: (isLoading: boolean) => {
          if (isLoading) {
            this.startTimeoutTimer();
          } else {
            this.closeLoadingDialog();
            this.clearTimeoutMessage();
          }
        },
      });
  }

  private openLoadingDialog(): void {
    this.loadingDialogRef ??= this.dialogService.openLoadingDialog();
  }

  private closeLoadingDialog(): void {
    this.loadingDialogRef?.close();
    this.loadingDialogRef = undefined;
  }

  private clearTimeoutMessage(): void {
    this.showTimeoutMessage.set(false);
    this.apiAttemptCount.set(0);
    if (this.requestTimer) {
      clearTimeout(this.requestTimer);
      this.requestTimer = null;
    }
  }

  private startTimeoutTimer(): void {
    if (!this.requestTimer) {
      this.requestTimer = setTimeout(() => {
        this.requestTimer = null;
        if (this.currentStep() === 'confirm-your-details' && this.orbitFormClaimService.formSubmission.isLoading()) {
          this.showTimeoutMessage.set(true);
          this.openLoadingDialog();
        }
      }, 3000);
    }
  }

  checkDateFormat(value: any) {
    if (typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(Date.parse(value));
  }
}
